/** Notifications page (v1 analytics.js toggleNotifPanel feed). Server-backed,
 * shared with v1: shows price-drop / new-lead / task rows, newest first. Click a
 * row to mark it read and jump to what it points at; "Mark all read" clears the
 * unread state. Reads + mark-read only (no emit from v2). */
import { useCallback, useEffect, useState } from "react";
import { markRead, notifRoute, notifFace, type Notif } from "./api";
import {
  NOTIF_EVENTS, prefOn, withPref, withAllPush, numScope, withNumScope, fetchPrefs, savePrefs, fetchVapid, enablePush, disablePush, pushSupported, iosNeedsHomeScreen, sendTestPush,
  type NotifPrefs,
} from "./push";
import { BackendError } from "../../lib/backend";

function fmtWhen(iso: string): string {
  if (!iso) return "";
  const n = Date.parse(iso);
  if (Number.isNaN(n)) return iso;
  return new Date(n).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function NotificationsPage({ load, onMarkRead, onOpenLead, onOpenPage, onOpenConvo, token }: {
  load: () => Promise<Notif[]>;
  onMarkRead: (which: { ids: string[] } | { all: true }) => Promise<void>;
  onOpenLead: (address: string) => void;
  onOpenPage: (page: string) => void;
  onOpenConvo?: (phone: string) => void;
  token: string | null;
}) {
  const [showPrefs, setShowPrefs] = useState(false);
  const [rows, setRows] = useState<Notif[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const refresh = useCallback(() => {
    setLoading(true); setErr("");
    void load().then(setRows).catch((e) => { setRows([]); setErr(e instanceof Error ? e.message : "Backend unreachable"); }).finally(() => setLoading(false));
  }, [load]);
  useEffect(() => { refresh(); }, [refresh]);

  const unread = (rows ?? []).filter((n) => !n.read).length;

  const open = (n: Notif) => {
    if (!n.read) { setRows((prev) => (prev ?? []).map((x) => (x.id === n.id ? { ...x, read: true } : x))); void onMarkRead({ ids: [n.id] }); }
    const r = notifRoute(n.link);
    if (r.kind === "lead") onOpenLead(r.address);
    else if (r.kind === "convo") (onOpenConvo ?? (() => onOpenPage("inbox")))(r.phone);
    else onOpenPage(r.page);
  };
  const allRead = () => { setRows((prev) => (prev ?? []).map((x) => ({ ...x, read: true }))); void onMarkRead({ all: true }); };

  return (
    <div className="leads exppage">
      <div className="exphead">
        <h1>Notifications</h1>
        <div className="expacts">
          {unread ? <span className="expcat">{unread} unread</span> : null}
          {unread ? <button className="btn slim" onClick={allRead}>Mark all read</button> : null}
          <button className="btn slim" onClick={() => setShowPrefs((s) => !s)}>⚙ Preferences</button>
          <button className="btn slim" onClick={refresh} disabled={loading}>{loading ? "…" : "↻ Refresh"}</button>
        </div>
      </div>

      {showPrefs ? <NotifSettings token={token} /> : null}
      {err ? <div className="expqbo">Couldn't reach the notifications backend ({err}). It's shared with v1 — the feed appears once the API is up.</div> : null}

      {rows === null ? (
        <div className="expempty">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="expempty">🔔 No notifications yet. Price drops, new leads, and task nudges land here.</div>
      ) : (
        <div className="ntlist">
          {rows.map((n) => {
            const face = notifFace(n.ev);
            return (
              <div key={n.id} className={"ntrow" + (n.read ? "" : " unread")} onClick={() => open(n)}>
                <span className={"nticon hue-" + face.hue}>{face.icon}</span>
                <div className="ntmain">
                  <div className="nttitle">{n.title || n.ev || "Notification"}</div>
                  {n.body ? <div className="ntbody">{n.body}</div> : null}
                  <div className="ntwhen">{fmtWhen(n.at)}</div>
                </div>
                {!n.read ? <span className="ntdot" /> : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Per-event in-app / push preferences + device push enrollment (v1
 * notifications.py prefs + web push). In-app + saving prefs are safe; enabling
 * push asks the browser and registers the push service worker. */
function NotifSettings({ token }: { token: string | null }) {
  const [prefs, setPrefs] = useState<NotifPrefs | null>(null);
  const [vapidEnabled, setVapidEnabled] = useState(false);
  const [vapidKey, setVapidKey] = useState("");
  const [pushOn, setPushOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    void fetchPrefs(token).then(setPrefs).catch(() => setPrefs({}));
    void fetchVapid(token).then((v) => { setVapidEnabled(v.enabled); setVapidKey(v.publicKey); }).catch(() => {});
    // reflect an existing push subscription on this device
    if (pushSupported()) {
      void navigator.serviceWorker.getRegistration("/push-sw.js")
        .then((reg) => reg?.pushManager.getSubscription())
        .then((sub) => setPushOn(!!sub))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (ev: string, ch: "inapp" | "push", on: boolean) => {
    if (!prefs) return;
    const next = withPref(prefs, ev, ch, on);
    setPrefs(next);
    void savePrefs(token, next).catch(() => setNote("Couldn't save that preference."));
  };

  const enable = async () => {
    setBusy(true); setNote(null);
    try {
      await enablePush(token, vapidKey);
      setPushOn(true);
      // Enrolling a device turns the push channel ON for every event — that's
      // what enrolling means. Untick any event below to opt back out.
      const next = withAllPush(prefs ?? {});
      setPrefs(next);
      void savePrefs(token, next).catch(() => {});
      setNote("Push enabled on this device ✓ — every event is on; untick any you don't want.");
    }
    catch (e) { setNote(e instanceof BackendError ? e.detail : e instanceof Error ? e.message : "Couldn't enable push."); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true); setNote(null);
    try { await disablePush(token); setPushOn(false); setNote("Push disabled on this device."); }
    finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true); setNote(null);
    try {
      const r = await sendTestPush(token);
      setNote(r.pushEnabled ? "Test sent — it should pop on every device with push enabled within a few seconds." : "Test logged in-app, but the server has no push keys configured.");
    } catch (e) { setNote(e instanceof BackendError ? e.detail : e instanceof Error ? e.message : "Couldn't send the test."); }
    finally { setBusy(false); }
  };

  const pushCol = vapidEnabled && pushSupported();

  return (
    <div className="ntprefs">
      <div className="ntprefshead">
        <strong>Notification preferences</strong>
        {pushSupported() ? (
          vapidEnabled ? (
            pushOn ? (
              <span className="ntpushacts">
                <button className="btn slim primary" onClick={test} disabled={busy}>{busy ? "…" : "Send test push"}</button>
                <button className="btn slim" onClick={disable} disabled={busy}>Disable push on this device</button>
              </span>
            ) : <button className="btn slim primary" onClick={enable} disabled={busy}>{busy ? "…" : "Enable push on this device"}</button>
          ) : <span className="sub">Push isn't configured on the server yet.</span>
        ) : iosNeedsHomeScreen()
          ? <span className="sub">📲 iPhone: open this site in Safari → Share → <b>Add to Home Screen</b>, then open the app from the icon and enable push here.</span>
          : <span className="sub">This browser doesn't support push.</span>}
      </div>
      {note ? <div className="inbxnote">{note}</div> : null}
      {!prefs ? <div className="sub">Loading preferences…</div> : (
        <>
        <div className="ntprefscope">
          <span>New text / call notifications for</span>
          <select value={numScope(prefs)} onChange={(e) => { const v = e.target.value === "own" ? "own" : "all"; const next = withNumScope(prefs, v); setPrefs(next); void savePrefs(token, next).catch(() => setNote("Couldn't save that preference.")); }}>
            <option value="all">All numbers</option>
            <option value="own">My number only</option>
          </select>
        </div>
        <div className="ntpreftable">
          <div className="ntprefrow head"><span>Event</span><span>In-app</span><span>Push</span></div>
          {NOTIF_EVENTS.map(({ ev, label }) => (
            <div key={ev} className="ntprefrow">
              <span>{label}</span>
              <span>
                <label className="ntswitch">
                  <input type="checkbox" className="ntswitchcb" checked={prefOn(prefs, ev, "inapp")} onChange={(e) => toggle(ev, "inapp", e.target.checked)} />
                  <span className="ntswitchtrack" aria-hidden="true" />
                  <span className="ntswitchknob" aria-hidden="true" />
                </label>
              </span>
              <span>
                <label className="ntswitch">
                  <input type="checkbox" className="ntswitchcb" disabled={!pushCol} checked={prefOn(prefs, ev, "push")} onChange={(e) => toggle(ev, "push", e.target.checked)} />
                  <span className="ntswitchtrack" aria-hidden="true" />
                  <span className="ntswitchknob" aria-hidden="true" />
                </label>
              </span>
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  );
}
